/**
 * Artifacts Pages
 *
 * Lookup by ID and upload flow for artifacts.
 * Note: Backend does not support listing artifacts - only lookup by ID.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';

import { RequestPanel, StatusBadge } from '../components/RequestPanel';
import { JsonViewer, Code } from '../components/JsonViewer';
import type {
  ApiResult,
  ArtifactWithVersions,
  ArtifactLinkListResponse,
  InitArtifactResponse,
} from '../lib/types';

export function Artifacts() {
  const navigate = useNavigate();
  const [showUpload, setShowUpload] = useState(false);

  // Lookup state
  const [lookupId, setLookupId] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState<'idle' | 'init' | 'upload' | 'hash' | 'commit' | 'done' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [initResult, setInitResult] = useState<ApiResult<InitArtifactResponse> | null>(null);
  const [commitResult, setCommitResult] = useState<ApiResult<ArtifactWithVersions> | null>(null);
  const [contentHash, setContentHash] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mimeType, setMimeType] = useState('text/plain');

  const handleLookup = async () => {
    if (!lookupId.trim()) return;

    setLookupLoading(true);
    setLookupError(null);

    try {
      const res = await apiClient.getArtifact(lookupId.trim());
      if (res.response.data) {
        navigate(`/console/artifacts/${lookupId.trim()}`);
      } else {
        setLookupError(res.response.error?.message ?? 'Artifact not found');
      }
    } catch {
      setLookupError('Failed to lookup artifact');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void handleLookup();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-detect mime type
      if (file.type) {
        setMimeType(file.type);
      } else if (file.name.endsWith('.json')) {
        setMimeType('application/json');
      } else if (file.name.endsWith('.txt')) {
        setMimeType('text/plain');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadError(null);
    setInitResult(null);
    setCommitResult(null);
    setContentHash(null);

    try {
      // Step 1: Init
      setUploadStep('init');
      const initRes = await apiClient.initArtifact({
        mimeType,
        filename: selectedFile.name,
      });
      setInitResult(initRes);

      if (!initRes.response.data) {
        throw new Error(initRes.response.error?.message ?? 'Init failed');
      }

      const { uploadUrl, pendingVersionId, storagePath, id } = initRes.response.data;

      // Step 2: Upload to presigned URL
      setUploadStep('upload');
      const uploadRes = await apiClient.uploadToPresignedUrl(uploadUrl, selectedFile, mimeType);

      if (!uploadRes.success) {
        throw new Error(uploadRes.error ?? 'Upload failed');
      }

      // Step 3: Compute hash
      setUploadStep('hash');
      const { sha256File } = await import('../lib/hash');
      const hash = await sha256File(selectedFile);
      setContentHash(hash);

      // Step 4: Commit
      setUploadStep('commit');
      const commitRes = await apiClient.commitArtifact(id, {
        contentHash: hash,
        contentSize: selectedFile.size,
        versionId: pendingVersionId,
        storagePath,
      });
      setCommitResult(commitRes);

      if (!commitRes.response.data) {
        throw new Error(commitRes.response.error?.message ?? 'Commit failed');
      }

      setUploadStep('done');
    } catch (err) {
      setUploadStep('error');
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setUploadStep('idle');
    setUploadError(null);
    setInitResult(null);
    setCommitResult(null);
    setContentHash(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 4 }}>
            Artifacts
          </h1>
          <p style={{ color: 'var(--slate-500)', fontSize: 14, margin: 0 }}>
            Lookup and upload transcript and audio artifacts
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowUpload(!showUpload); resetUpload(); }}>
          {showUpload ? (
            <>
              <iconify-icon icon="lucide:x" width="16" height="16"></iconify-icon>
              Cancel Upload
            </>
          ) : (
            <>
              <iconify-icon icon="lucide:upload" width="16" height="16"></iconify-icon>
              Upload Artifact
            </>
          )}
        </button>
      </div>

      {/* Lookup by ID */}
      <div className="card">
        <h2>Lookup by ID</h2>
        <p className="text-muted text-small" style={{ marginBottom: 16 }}>
          Enter an artifact ID to view its details. The backend does not support listing all artifacts.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            className="input"
            placeholder="Enter Artifact ID (ULID format)"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            onKeyDown={handleKeyPress}
            style={{ flex: 1 }}
          />
          <button
            className="btn btn-secondary"
            onClick={() => void handleLookup()}
            disabled={!lookupId.trim() || lookupLoading}
          >
            {lookupLoading ? 'Looking up...' : 'Lookup'}
          </button>
        </div>
        {lookupError && (
          <div className="error-box" style={{ marginTop: 12 }}>
            {lookupError}
          </div>
        )}
      </div>

      {/* Upload form */}
      {showUpload && (
        <div className="card">
          <h2>Upload Artifact</h2>
          <p className="text-muted text-small" style={{ marginBottom: 16 }}>
            Upload flow: Init → Upload to S3 → Compute SHA-256 → Commit
          </p>

          <div className="form-group">
            <label className="label">File</label>
            <input
              ref={fileInputRef}
              type="file"
              className="input"
              onChange={handleFileChange}
              disabled={uploading}
            />
          </div>

          <div className="form-group">
            <label className="label">MIME Type</label>
            <select
              className="input"
              value={mimeType}
              onChange={(e) => setMimeType(e.target.value)}
              disabled={uploading}
            >
              <option value="text/plain">text/plain (Transcript)</option>
              <option value="application/json">application/json (JSON Transcript)</option>
              <option value="audio/wav">audio/wav</option>
              <option value="audio/mp3">audio/mp3</option>
              <option value="audio/mpeg">audio/mpeg</option>
            </select>
          </div>

          <button
            className="btn btn-primary"
            onClick={() => void handleUpload()}
            disabled={!selectedFile || uploading}
          >
            {uploading ? `Uploading (${uploadStep})...` : 'Upload'}
          </button>

          {/* Progress indicator */}
          {uploadStep !== 'idle' && (
            <div style={{ marginTop: 16 }}>
              <div className="flex gap-2 items-center" style={{ marginBottom: 8 }}>
                <span style={{ color: uploadStep === 'init' ? '#2563eb' : uploadStep === 'error' ? '#dc2626' : '#16a34a' }}>
                  {uploadStep === 'init' ? '⏳' : '✓'} Init
                </span>
                <span style={{ color: uploadStep === 'upload' ? '#2563eb' : uploadStep === 'init' ? '#666' : uploadStep === 'error' ? '#dc2626' : '#16a34a' }}>
                  {uploadStep === 'upload' ? '⏳' : uploadStep === 'init' ? '○' : '✓'} Upload
                </span>
                <span style={{ color: uploadStep === 'hash' ? '#2563eb' : ['init', 'upload'].includes(uploadStep) ? '#666' : uploadStep === 'error' ? '#dc2626' : '#16a34a' }}>
                  {uploadStep === 'hash' ? '⏳' : ['init', 'upload'].includes(uploadStep) ? '○' : '✓'} Hash
                </span>
                <span style={{ color: uploadStep === 'commit' ? '#2563eb' : ['init', 'upload', 'hash'].includes(uploadStep) ? '#666' : uploadStep === 'error' ? '#dc2626' : '#16a34a' }}>
                  {uploadStep === 'commit' ? '⏳' : ['init', 'upload', 'hash'].includes(uploadStep) ? '○' : '✓'} Commit
                </span>
              </div>

              {contentHash && (
                <div className="text-small">
                  <strong>Content Hash:</strong> <code className="mono">{contentHash}</code>
                </div>
              )}

              {uploadError && (
                <div className="error-box" style={{ marginTop: 8 }}>
                  {uploadError}
                </div>
              )}

              {uploadStep === 'done' && commitResult?.response.data && (
                <div className="success-box" style={{ marginTop: 8 }}>
                  Artifact uploaded successfully!{' '}
                  <Link to={`/console/artifacts/${commitResult.response.data.id}`}>View artifact →</Link>
                </div>
              )}
            </div>
          )}

          {initResult && <RequestPanel result={initResult} />}
          {commitResult && <RequestPanel result={commitResult} />}
        </div>
      )}
    </div>
  );
}

export function ArtifactDetail() {
  const { id } = useParams<{ id: string }>();
  const [artifact, setArtifact] = useState<ArtifactWithVersions | null>(null);
  const [links, setLinks] = useState<ArtifactLinkListResponse | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<ArtifactWithVersions> | null>(null);
  const [contentResult, setContentResult] = useState<ApiResult<string> | null>(null);

  const loadArtifact = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    const res = await apiClient.getArtifact(id);
    setResult(res);
    if (res.response.data) {
      setArtifact(res.response.data);

      // Load content for text artifacts
      if (res.response.data.mimeType.startsWith('text/') || res.response.data.mimeType === 'application/json') {
        const contentRes = await apiClient.getArtifactContentAsText(id);
        setContentResult(contentRes);
        if (contentRes.response.data) {
          setContent(contentRes.response.data);
        }
      }
    }

    // Load links
    const linksRes = await apiClient.listArtifactLinks({ artifactId: id });
    if (linksRes.response.data) {
      setLinks(linksRes.response.data);
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    void loadArtifact();
  }, [loadArtifact]);

  return (
    <div>
      <Link
        to="/console/artifacts"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--slate-500)',
          fontSize: 14,
          textDecoration: 'none',
          marginBottom: 16,
        }}
      >
        <iconify-icon icon="lucide:arrow-left" width="16" height="16"></iconify-icon>
        Back to Artifacts
      </Link>

      <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 24 }}>
        Artifact Detail
      </h1>

      {artifact && (
        <>
          <div className="card">
            <h2>Summary</h2>
            <table className="table" style={{ maxWidth: 600 }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 500, width: 180 }}>ID</td>
                  <td><Code>{artifact.id}</Code></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Status</td>
                  <td><StatusBadge status={artifact.status} /></td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>MIME Type</td>
                  <td className="mono">{artifact.mimeType}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Filename</td>
                  <td className="mono">{artifact.filename || '-'}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Company</td>
                  <td className="mono">{artifact.tenantId}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Created At</td>
                  <td>{new Date(artifact.createdAt).toLocaleString()}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Created By</td>
                  <td className="mono">{artifact.createdBy}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card">
            <h2>Versions</h2>
            {artifact.versions.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Version ID</th>
                    <th>Content Hash</th>
                    <th>Size</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {artifact.versions.map((v) => (
                    <tr key={v.id}>
                      <td><Code>{v.id.slice(0, 12)}...</Code></td>
                      <td className="mono text-small">{v.contentHash.slice(0, 16)}...</td>
                      <td>{(v.contentSize / 1024).toFixed(2)} KB</td>
                      <td className="text-muted">{new Date(v.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-muted">No versions found.</p>
            )}
          </div>

          {links && links.items.length > 0 && (
            <div className="card">
              <h2>Links</h2>
              <table className="table">
                <thead>
                  <tr>
                    <th>Link Type</th>
                    <th>Assessment</th>
                    <th>Artifact Version</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {links.items.map((link) => (
                    <tr key={link.id}>
                      <td><Code>{link.linkType}</Code></td>
                      <td>
                        <Link to={`/console/assessments/${link.assessmentId}`}>
                          <Code>{link.assessmentId.slice(0, 12)}...</Code>
                        </Link>
                      </td>
                      <td className="mono text-small">
                        {link.artifactVersionId.slice(0, 12)}...
                      </td>
                      <td className="text-muted text-small">
                        {new Date(link.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {content !== null && (
            <div className="card">
              <h2>Content</h2>
              {artifact.mimeType === 'application/json' ? (
                <JsonViewer data={JSON.parse(content) as Record<string, unknown>} maxHeight={500} />
              ) : (
                <pre style={{ maxHeight: 500, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                  {content.slice(0, 10000)}
                  {content.length > 10000 && '\n... (truncated)'}
                </pre>
              )}
            </div>
          )}

          <div className="card">
            <h2>Actions</h2>
            <div className="flex gap-2">
              {artifact.versions[0] && (
                <Link
                  to={`/console/scores?transcriptArtifactVersionId=${artifact.versions[0].id}`}
                  className="btn btn-primary"
                >
                  Generate Score
                </Link>
              )}
            </div>
          </div>
        </>
      )}

      <RequestPanel result={result} loading={loading} />
      {contentResult && <RequestPanel result={contentResult} />}
    </div>
  );
}
